import { OrderAddress, Session, SessionEvent } from '../types/checkout';
import { common } from './upstream';
import { SessionOrGap } from './sessions/types';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import { isGap, Gap } from '../helpers/paging';
import { parseISOStrict } from '../helpers/date';

export const getVatAmount = (session?: Session): number | undefined => {
    if (session && session.order && session.order.vat_amount) {
        return session.order.vat_amount;
    }
    return undefined;
};

export const getItems = (session: Session) => {
    return common.getItems(session);
};

const customerName = (address: OrderAddress) => {
    const names = [address.first_name, address.last_name];
    const fullName = names.filter((x) => x).join(' ');
    return fullName || address.phone_number || address.email || '';
};

export const getCustomerName = (session: Session) => {
    if (session.order.billing_address) {
        return customerName(session.order.billing_address);
    }
    if (session.order.shipping_address) {
        return customerName(session.order.shipping_address);
    }
    return '';
};

export const getNextPageArgs = (sessions: SessionOrGap[]): { [key: string]: string } => {
    if (sessions.length === 0) {
        return {};
    }
    const last = sessions[sessions.length - 1];
    return { starting_after: last.id || '' };
};

export const getGapPageArgs = (sessions: SessionOrGap[], id: string): { [key: string]: string } => {
    const gap = sessions.find((s) => isGap(s) && s.id === id);
    if (!gap) {
        return {};
    }
    return { starting_after: gap.id || '' };
};

export const isSession = (obj: SessionOrGap): obj is Session => {
    return (obj as Gap).gap !== true;
};

export const isSessionExpired = (session: Session) => {
    if (!session) {
        return false;
    }
    const lastEventName = getLastSessionEventName(session);
    const canExpire = !SESSION_END_STATES.includes(lastEventName);
    const expiresAt = session.expires_at && parseISOStrict(session.expires_at);
    const isExpired = !!expiresAt && differenceInMilliseconds(expiresAt, new Date()) < 0;
    const hasTransactionAlready = !!session.transaction_id;

    return !hasTransactionAlready && canExpire && isExpired;
};

export const getCurrentEvent = (session: Session): SessionEvent => {
    if (session.events) {
        const idx = session.events.length - 1;
        if (idx >= 0) {
            const event = session.events[idx];
            const sessionExpired = isSessionExpired(session);
            if (sessionExpired) {
                return {
                    name: 'EXPIRED',
                    created_at: new Date().toISOString(),
                };
            }
            if (event) {
                return event;
            }
        }
    }
    return {
        name: 'UNKNOWN',
        created_at: new Date().toISOString(),
    };
};

export const getLastSessionEventName = (session: Session): string => {
    const lastEvent = session.events && session.events[session.events.length - 1];
    if (lastEvent) {
        return lastEvent.name || '';
    }
    return '';
};

export const SESSION_END_STATES = [
    'EXPIRED',
    'FAILED',
    'DECLINED',
    'UNKNOWN',
    'AUTHORIZED',
    'CANCELLED',
    'ON_HOLD',
    'AUTH_CALLBACK_SENT',
];

export const SYNTHETIC_SESSION_END_STATES = [
    'EXPIRED',
    'FAILED',
    'DECLINED',
    'UNKNOWN',
    'AUTHORIZED',
    'CAPTURED',
    'CAPTURE_FAILED',
    'CANCELLED',
    'AUTH_CALLBACK_SENT',
];
